import { DepositIcon, MoreOne, TooltipIcon, TradeIcon, TransferData, WithdrawIcon } from "assets/Icon";
import { Box, Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import ActionTable from "./Component/ActionTable";
import Coin from "./Component/Coin";
import CoinCardWithColumn from "./Component/CoinCardWithColumn";
import CoinCardWithRow from "./Component/CoinCardWithRow";
import { GetSpotWalletList } from "store/api/wallet";
import NumberFormats from "components/NumberFormats";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import { TextCell } from "./Component/TextCell";
import WalletActions from "components/WalletActions";
import http from "config/http";
import useIsMobileView from "hooks/useIsMobileView";
import useNotify from "hooks/useNotify";
import { VALUE_BALANCE } from "pages/Wallet/constants";
import { ROUTE_PATHS } from "routes/paths";

const headers = [
  {
    key: "coin",
    align: "left",
    name: "Coin",
    sortable: true,
    sortKey: "coinName",
  },
  {
    key: "total",
    align: "left",
    name: "Total",
    sortable: true,
    sortKey: "totalBalance",
  },
  {
    key: "available",
    align: "left",
    name: "Available",
    sortable: true,
    sortKey: "availableBalance",
  },

  {
    key: "limit",
    align: "left",
    name: "Limit",
    sortable: true,
    sortKey: "tradeLimit",
  },

  {
    key: "valueBalance",
    align: "left",
    name: "Value Balance",
    sortable: true,
    sortKey: "valueBalance",
  },

  {
    key: "actions",
    align: "center",
    name: "Actions",
  },
];

export function TableSpot() {
  const notify = useNotify();
  const isMobileView = useIsMobileView();
  const theme = useTheme();

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const transfer = searchParams.get("transfer");

  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(true);
  const [coinList, setCoinList] = useState();
  const [activeView, setActiveView] = useState("list");
  const [valueBalance, seValueBalance] = useState(0);

  const [showWalletAction, setShowWalletAction] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);

  const handleShowWalletAction = (data) => {
    setSelectedCoin(data);
    setShowWalletAction(true);
  };

  const walletActionItems = [
    {
      icon: (
        <WithdrawIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Withdraw",
      onClick: (data) => navigate(`/withdraw/${data.coinId}`),
    },
    {
      icon: (
        <DepositIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Deposit",
      onClick: (data) => navigate(`/deposit/${data.coinId}`),
    },
    {
      icon: (
        <TradeIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Trade",
      onClick: (data) => {
        const USDT = 3;
        const Toman = 4;

        const url =
          data.coinId === USDT || data.coinId === Toman
            ? ROUTE_PATHS.trade.basic(USDT, Toman)
            : ROUTE_PATHS.trade.basic(data.coinId, USDT);

        navigate(url);
      },
    },
    {
      icon: <TransferData color={theme.palette.secondary.main} />,
      text: "Transfer",
      onClick: (data) => {
        setSearchParams({
          transfer: data?.coinId,
          transferSideId: 2,
        });
      },
    },
  ];

  const getSpotWalletList = async () => {
    try {
      setLoading(true);

      const { data } = await http.get(GetSpotWalletList(limit));

      setCoinList(data?.data);
      setLoading(false);
    } catch (e) {
      notify(e?.response?.data?.message, "error");
    }
  };

  const coinHeaders = useMemo(() => {
    if (valueBalance === VALUE_BALANCE.USDT) {
      return [
        {
          key: "totalBalance",
          bold: true,
          text: true,
          title: "Total",
        },
        {
          key: "tradeLimit",
          text: true,
          bold: true,
          title: "Limit",
        },
        {
          key: "availableBalance",
          bold: true,
          text: true,
          title: "Available",
        },
        {
          key: "totalBalanceUSDT",
          bold: true,
          text: true,
          title: "Value Blnc (USDT)",
        },
      ];
    } else {
      return [
        {
          key: "totalBalance",
          bold: true,
          text: true,
          title: "Total",
        },
        {
          key: "tradeLimit",
          text: true,
          bold: true,
          title: "Limit",
        },
        {
          key: "availableBalance",
          bold: true,
          text: true,
          title: "Available",
        },

        {
          key: "totalBalanceToman",
          bold: true,
          text: true,
          title: "Value Blnc (Toman)",
        },
      ];
    }
  }, [valueBalance]);

  useEffect(() => {
    getSpotWalletList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, transfer]);

  const formattedCoinList = coinList?.map((data) => {
    return {
      data: data,
      coin: <Coin coin={data} />,

      total: (
        <>

          {data?.totalBalanceComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.totalBalanceComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <Box display={"flex"} alignItems={"center"} width={"fit-content"} gap={1}>
                <TextCell
                  item={<NumberFormats value={data?.totalBalance} />}
                  component="p"
                />
                <Box mb={"0px"}>
                  <TooltipIcon color={theme.palette.text.main} />
                </Box>
              </Box>
            </Tooltip>
          ) : <TextCell
            item={<NumberFormats value={data?.totalBalance} />}
            component="p"
          />
          }
        </>
      ),

      available: (
        <>

          {data?.availableBalanceComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.availableBalanceComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <Box display={"flex"} alignItems={"center"} width={"fit-content"} gap={1}>
                <TextCell
                  item={<NumberFormats value={data?.availableBalance} />}
                  component="p"
                />
                <Box mb={"0px"}>
                  <TooltipIcon color={theme.palette.text.main} />
                </Box>
              </Box>
            </Tooltip>
          ) : <TextCell
            item={<NumberFormats value={data?.availableBalance} />}
            component="p"
          />}
        </>
      ),

      limit: (
        <>
          {data?.tradeLimitComment ? (
            <Tooltip
              title={
                <TextCell
                  color="secondary"
                  item={data?.tradeLimitComment}
                  component="p"
                />
              }
              placement="top"
              arrow
            >
              <Box display={"flex"} alignItems={"center"} width={"fit-content"} gap={1}>
                <TextCell item={data?.tradeLimit} component="p" />
                <Box mb={"0px"}>
                  <TooltipIcon color={theme.palette.text.main} />
                </Box>
              </Box>
            </Tooltip>
          ) : <TextCell item={data?.tradeLimit} component="p" />}
        </>
      ),

      valueBalance: (
        <Typography component="span" color={"text.main"} variant="h5">
          <NumberFormats
            value={
              valueBalance === VALUE_BALANCE.USDT
                ? data?.totalBalanceUSDT
                : data?.totalBalanceToman
            }
          />
          <Typography
            variant="subtitle1"
            component="span"
            color={"text.secondary"}
            pl={1}
          >
            {valueBalance === VALUE_BALANCE.USDT ? "USDT" : "Toman"}
          </Typography>
        </Typography>
      ),

      actions: (
        <IconButton color="text" onClick={() => handleShowWalletAction(data)}>
          <MoreOne color={theme.palette.text.main} />
        </IconButton>
      ),
    };
  });

  return (
    <>
      <ActionTable
        setLimit={setLimit}
        limit={limit}
        setActiveView={setActiveView}
        activeView={activeView}
        valueBalance={valueBalance}
        seValueBalance={seValueBalance}
        isSpot={true}
      />

      {isMobileView ? (
        <>
          {" "}
          {coinList?.map((coin) => (
            <CoinCardWithRow
              coin={coin}
              headers={coinHeaders}
              onClick={(coin) => {
                navigate(`/wallet/spot/${coin?.coinId}`);
              }}
            />
          ))}
        </>
      ) : (
        <>
          {activeView === "list" ? (
            <>
              {!loading ? (
                <Table headers={headers} items={formattedCoinList} />
              ) : (
                <TableSkeleton />
              )}
            </>
          ) : (
            <Grid container spacing={1.25}>
              {coinList?.map((coin) => (
                <Grid item lg={3} xs={6} sm={6} md={4} key={coin?.id}>
                  <CoinCardWithColumn
                    coin={coin}
                    headers={coinHeaders}
                    onAction={(data) => handleShowWalletAction(data)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      <WalletActions
        hideBookmark
        items={walletActionItems}
        open={showWalletAction}
        coin={selectedCoin}
        onClose={() => {
          setShowWalletAction(false);
        }}
      />
    </>
  );
}

export default TableSpot;
